import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { AppThunk } from '../../store'
import { AtLeastOne } from '../../utils/type_utils'
import {
  EMPTY_SERVER_CONFIG_MAP,
  ServerKeys,
  serverMap,
  ServersState
} from './types'

export const generateEmptyServerState = () => {
  const emptyState: ServersState = {
    serverMap,
    urls: {} as ServersState['urls'],
    connections: {} as ServersState['connections'],
    versions: {} as ServersState['versions'],
    configs: EMPTY_SERVER_CONFIG_MAP
  }

  for (const key in serverMap) {
    emptyState.urls[key as ServerKeys] = {
      url: '',
      websocketUrl: ''
    }
    emptyState.connections[key as ServerKeys] = {
      connected: false,
      hasMadeSuccessfulConnection: false
    }
  }

  return emptyState
}

export const initialServersState: ServersState = {
  ...generateEmptyServerState()
}

type AtLeastOneServerConfig = AtLeastOne<ServersState['configs']>
type AtLeastOneServerUrls = AtLeastOne<ServersState['urls']>
type AtLeastOneServerVersion = AtLeastOne<ServersState['versions']>

export const serversSlice = createSlice({
  name: 'servers',
  initialState: initialServersState,
  reducers: {
    setServerUrls: (state, action: PayloadAction<AtLeastOneServerUrls>) => {
      Object.keys(action.payload).forEach((key) => {
        const serverKey = key as ServerKeys
        state.urls[serverKey] = action.payload[serverKey]!
      })
    },
    setServersConnected: (
      state,
      action: PayloadAction<{ key: ServerKeys; connected: boolean }>
    ) => {
      const serverKey = action.payload.key

      state.connections[serverKey] = {
        connected: action.payload.connected,
        hasMadeSuccessfulConnection:
          action.payload.connected ||
          state.connections[serverKey].hasMadeSuccessfulConnection
            ? true
            : false
      }
    },
    setServerConfigs: (
      state,
      action: PayloadAction<AtLeastOneServerConfig>
    ) => {
      Object.keys(action.payload).forEach((key) => {
        const serverKey = key as ServerKeys
        state.configs[serverKey] = action.payload[serverKey]!
      })
    },
    setServerVersions: (
      state,
      action: PayloadAction<AtLeastOneServerVersion>
    ) => {
      Object.keys(action.payload).forEach((key) => {
        const serverKey = key as ServerKeys
        state.versions[serverKey] = action.payload[serverKey]!
      })
    }
  }
})

export const reducer = serversSlice.reducer

export const {
  setServersConnected,
  setServerConfigs,
  setServerUrls,
  setServerVersions
} = serversSlice.actions

export function fetchServerVersion<k extends ServerKeys>(
  serverKey: k
): AppThunk {
  return async (dispatch, getState) => {
    const state = getState()
    let baseUrl = state.servers.urls[serverKey]?.url
    const versionPath = serverMap[serverKey].endpoints.version

    if (!baseUrl) return

    // Fix: Remove trailing slash to prevent double slash issue
    if (baseUrl.endsWith('/')) {
      baseUrl = baseUrl.slice(0, -1)
    }

    const fullUrl = baseUrl + versionPath

    try {
      const response = await fetch(fullUrl)

      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      const version = await response.text()

      dispatch(
        setServersConnected({
          key: serverKey,
          connected: true
        })
      )
      dispatch(
        setServerVersions({
          [serverKey]: version.trim() // Also trim the newline character
        } as AtLeastOneServerVersion)
      )
    } catch (err) {
      console.error(`❌ Version fetch failed for ${serverKey}:`, err)
      dispatch(
        setServersConnected({
          key: serverKey,
          connected: false
        })
      )
    }
  }
}

export function fetchServerConfig(serverKey: ServerKeys): AppThunk {
  return async (dispatch, getState) => {
    const state = getState()
    const baseUrl = state.servers.urls[serverKey]?.url
    const configPath = serverMap[serverKey].endpoints.config

    if (!baseUrl) return

    try {
      const response = await fetch(baseUrl + configPath)
      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      const config = await response.json()

      dispatch(
        setServersConnected({
          key: serverKey,
          connected: true
        })
      )
      dispatch(
        setServerConfigs({
          [serverKey]: config
        } as AtLeastOneServerConfig)
      )
    } catch (err) {
      dispatch(
        setServersConnected({
          key: serverKey,
          connected: false
        })
      )
      console.error(`Config fetch failed for ${serverKey}`, err)
    }
  }
}
